import { useState, useEffect, useCallback, useMemo } from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { StatTile } from '../../components/ui/StatTile';
import { Toast } from '../../components/ui/Toast';
import { Switch } from '../../components/ui/Switch';
import { formatIsraelTime, formatIsraelDate } from '../../utils/formatters';
import { api } from '../../lib/api';

// Icons used in stat tiles, section headers and the empty state.
const PulseIcon = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M2.5 10.5h3l2-5 3 9 2-4h4.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const WarnIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M10 7v4M10 13.5h.01" strokeLinecap="round" />
    <path d="M8.6 3.3 1.9 15a1.5 1.5 0 0 0 1.3 2.2h13.6a1.5 1.5 0 0 0 1.3-2.2L11.4 3.3a1.5 1.5 0 0 0-2.8 0Z" strokeLinejoin="round" />
  </svg>
);
const DropIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M10 2.5s5 6 5 9.5a5 5 0 01-10 0c0-3.5 5-9.5 5-9.5z" strokeLinejoin="round" />
  </svg>
);
// Unplugged/setup icon — used for products that have simply never sent data
// (no AppScript configured yet), distinct from the red "something broke" icons.
const PlugIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M7 3v3M13 3v3M6 7h8v3a4 4 0 01-4 4 4 4 0 01-4-4V7z" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 14v3" strokeLinecap="round" />
  </svg>
);
const CheckIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M5 10.5l3.5 3.5L15 6.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const fmtWhen = formatIsraelTime;
const fmtDate = formatIsraelDate;

// Small family badge — same neutral pill style used across the app's tables.
function FamilyBadge({ family }) {
  if (!family) return null;
  return (
    <span className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:bg-white/10 dark:text-slate-400">
      {family}
    </span>
  );
}

// One alert card — used for real, actionable issues only (Missing Data / Usage Drop).
function AlertCard({ issue, icon, message }) {
  return (
    <div className="flex items-start gap-3 rounded-xl bg-red-50 px-4 py-3 ring-1 ring-red-100 dark:bg-red-500/10 dark:ring-red-500/20">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-300">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-semibold text-navy-800 dark:text-slate-100">{issue.product}</span>
          <FamilyBadge family={issue.family} />
        </div>
        <div className="mt-1 text-xs leading-relaxed text-red-700/90 dark:text-red-300/80">{message}</div>
      </div>
    </div>
  );
}

const missingDataMessage = (issue) => (
  <>No usage reported on <b>{fmtDate(issue.date)}</b> — the AppScript may have failed for this product.</>
);
const dataDropMessage = (issue) => (
  <><b>{issue.prev} → {issue.current}</b> on {fmtDate(issue.date)} — usage dropped sharply vs the previous day. Please check the AppScript.</>
);

// A titled group of REAL alerts, always shown — with a green "clear" state when
// this check found nothing, so each check type has a permanent, dedicated section.
function AlertSection({ title, description, icon, issues, renderMessage }) {
  const clear = !issues.length;
  const tone = clear
    ? 'bg-green-100 text-green-600 dark:bg-green-500/15 dark:text-green-300'
    : 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-300';
  return (
    <div>
      <div className="mb-3.5 flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-slate-900/5 dark:bg-white/5 dark:ring-white/10">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}>
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-navy-800 dark:text-slate-100">{title}</h3>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{description}</p>
        </div>
        <div className={`shrink-0 text-right ${clear ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          <div className="text-2xl font-bold leading-none">{issues.length}</div>
          <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
            {issues.length === 1 ? 'product' : 'products'}
          </div>
        </div>
      </div>
      {clear ? (
        <div className="flex items-center gap-2.5 rounded-xl bg-green-50 px-4 py-3 text-xs text-green-700 ring-1 ring-green-100 dark:bg-green-500/10 dark:text-green-300 dark:ring-green-500/20">
          <CheckIcon className="h-4 w-4 shrink-0" />
          None found in the last check.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-2">
          {issues.map((issue, i) => (
            <AlertCard key={`${issue.product}-${i}`} issue={issue} icon={icon} message={renderMessage(issue)} />
          ))}
        </div>
      )}
    </div>
  );
}

// Separate, calmer card for products that have simply never sent usage data —
// most likely awaiting their AppScript. Kept apart from the real alerts above so
// an expected "still onboarding" status never reads as urgent. Uses the shared
// Table component for built-in search + pagination — this list can be long.
function PendingSetupCard({ issues }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card
      title="Pending AppScript Setup"
      subtitle="Products awaiting their AppScript — informational, not an alert."
      actions={<Switch checked={expanded} onChange={setExpanded} label={expanded ? `Hide list (${issues.length})` : `Show list (${issues.length})`} />}
    >
      {expanded && (
        issues.length ? (
          <Table
            columns={[
              {
                key: 'product', label: 'Product',
                render: (v) => (
                  <span className="flex items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400">
                      <PlugIcon className="h-3 w-3" />
                    </span>
                    <span className="font-medium text-navy-800 dark:text-slate-100">{v}</span>
                  </span>
                ),
              },
              { key: 'family', label: 'Family', render: (v) => <FamilyBadge family={v} /> },
              { key: 'note', label: '', align: 'right', render: () => <span className="text-xs text-slate-400 dark:text-slate-500">No data in last 7 days</span> },
            ]}
            rows={issues}
            rowKey={(r) => r.product}
            pageSize={10}
            searchable
            searchPlaceholder="Search product or family…"
          />
        ) : (
          <p className="text-xs text-slate-400 dark:text-slate-500">Nothing pending — every product has sent data recently.</p>
        )
      )}
    </Card>
  );
}

export default function SystemStatus() {
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    api.get('/api/system-status')
      .then(setLatest)
      .catch((e) => setMsg({ type: 'error', text: e.message }))
      .finally(() => { if (!silent) setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!msg) return undefined;
    const t = setTimeout(() => setMsg(null), 4500);
    return () => clearTimeout(t);
  }, [msg]);

  const checkNow = async () => {
    setRunning(true);
    setMsg(null);
    try {
      await api.post('/api/system-status/run');
      setMsg({ type: 'success', title: 'Check complete', text: 'System status has been refreshed.' });
      load(true);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setRunning(false);
    }
  };

  const issues = latest?.issues || [];
  const { activeGaps, pendingSetup, drops } = useMemo(() => {
    const missing = issues.filter((i) => i.type === 'missing_data');
    return {
      activeGaps: missing.filter((i) => !i.noAppScript),
      pendingSetup: missing.filter((i) => i.noAppScript),
      drops: issues.filter((i) => i.type === 'data_drop'),
    };
  }, [issues]);

  return (
    <AppShell title="System Status">
      <Toast msg={msg} onDismiss={() => setMsg(null)} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <StatTile label="Last checked" value={latest ? fmtWhen(latest.checkedAt) : '—'} icon={<PulseIcon />} />
        <StatTile label="Missing data" value={activeGaps.length} tone={activeGaps.length ? 'red' : 'green'} icon={<WarnIcon className="h-5 w-5" />} />
        <StatTile label="Usage drops" value={drops.length} tone={drops.length ? 'red' : 'green'} icon={<DropIcon className="h-5 w-5" />} />
        <StatTile label="Pending setup" value={pendingSetup.length} tone={pendingSetup.length ? 'slate' : 'green'} icon={<PlugIcon className="h-5 w-5" />} />
      </div>

      <Card
        title="Data health check"
        subtitle="Daily check for missing usage data and sharp usage drops."
        actions={(
          <Button onClick={checkNow} disabled={running} className="gap-1.5">
            <svg className={`h-4 w-4 ${running ? 'animate-spin' : ''}`} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M16 10a6 6 0 11-1.76-4.24M16 4v4h-4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {running ? 'Checking…' : 'Check now'}
          </Button>
        )}
      >
        {loading ? <Spinner /> : (
          <div className="space-y-6">
            <AlertSection
              title="Missing Data"
              description="Active products with no usage today — possible AppScript failure."
              icon={<WarnIcon />}
              issues={activeGaps}
              renderMessage={missingDataMessage}
            />
            <AlertSection
              title="Usage Drop"
              description="50%+ drop vs. yesterday."
              icon={<DropIcon />}
              issues={drops}
              renderMessage={dataDropMessage}
            />
          </div>
        )}
      </Card>

      {!loading && <PendingSetupCard issues={pendingSetup} />}
    </AppShell>
  );
}
